/**
 * Section Configurations for Contextual Sidebar Navigation
 *
 * Defines the sections that have contextual sub-navigation panels.
 * When a user clicks on these sections, the sidebar content switches
 * to show the sub-items with a back button.
 */

import {
  Cog6ToothIcon,
  BuildingOffice2Icon,
  UsersIcon,
  CpuChipIcon,
  EnvelopeIcon,
  ClockIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  LinkIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  PhoneIcon,
  SwatchIcon,
  BeakerIcon,
  CommandLineIcon,
  AdjustmentsHorizontalIcon,
  ServerStackIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  BanknotesIcon,
  PresentationChartBarIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  AdjustmentsVerticalIcon,
  ExclamationTriangleIcon,
  ArrowsRightLeftIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  ScaleIcon,
  TableCellsIcon,
  VideoCameraIcon,
  CalendarDaysIcon,
  CircleStackIcon,
  EyeIcon,
  PlayIcon,
  FolderIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import type { ContextPanelItem } from '../ui/sidebar-context-panel';
import type { ActiveSection } from '../../contexts/NavigationContext';
import { isFrontendPageEnabled } from '../../shared/lib/applets';

/* ============================================
   Section Configuration Type
   ============================================ */

export type SectionNavItem = ContextPanelItem & {
  /** Optional modular page key for applet/page-level visibility gating */
  pageKey?: string;
  /** Require exact path match instead of treating the item as a parent route */
  exact?: boolean;
  /** Visually indicate this item leaves the current contextual surface */
  outbound?: boolean;
}

/** An accordion group within a section (e.g., each applet in Monthly Close) */
export interface SectionNavGroup {
  isGroup: true;
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Path prefix that marks this group as active */
  basePath: string;
  /** Where to navigate when clicking the group header from outside the group */
  defaultPath: string;
  pageKey?: string;
  requiredPermissions?: string[];
  items: SectionNavItem[];
}

export type SectionConfigItem = SectionNavItem | SectionNavGroup;

export interface SectionConfig {
  /** Section identifier (matches ActiveSection type) */
  id: ActiveSection
  /** Display title */
  title: string
  /** Section icon */
  icon: React.ComponentType<{ className?: string }>
  /** Base path that activates this section */
  basePath: string
  /** Sub-navigation items (flat items or accordion groups) */
  items: SectionConfigItem[]
}

/* ============================================
   AI Recruiter Section
   ============================================ */

export const aiRecruiterConfig: SectionConfig = {
  id: 'ai-recruiter',
  title: 'AI Recruiter',
  icon: UserGroupIcon,
  basePath: '/talent',
  items: [
    {
      label: 'Search Templates',
      path: '/talent/analysis-config',
      icon: MagnifyingGlassIcon,
      pageKey: 'talent-intelligence',
    },
    {
      label: 'Search Results',
      path: '/talent/outreach',
      icon: UserGroupIcon,
      pageKey: 'talent-intelligence',
    },
    {
      label: 'Email Templates',
      path: '/talent/email-templates',
      icon: EnvelopeIcon,
      pageKey: 'talent-intelligence',
    },
    {
      label: 'Email Integration',
      path: '/talent/email-integration',
      icon: AdjustmentsHorizontalIcon,
      pageKey: 'talent-intelligence',
      requiredPermissions: ['admin:settings:read', 'admin:settings:update', 'platform:admin'],
    },
    {
      label: 'Blueprints & DNA',
      path: '/talent/configuration',
      icon: SparklesIcon,
      pageKey: 'talent-intelligence',
    },
    {
      label: 'Thank-You Videos',
      path: '/talent/thank-you-videos',
      icon: VideoCameraIcon,
      pageKey: 'candidate-thank-you-videos',
    },
    {
      label: 'Search History',
      path: '/talent/history',
      icon: ClipboardDocumentListIcon,
      pageKey: 'talent-intelligence',
    },
    {
      label: 'Role Builder',
      path: '/talent/role-builder',
      icon: DocumentTextIcon,
      pageKey: 'role-builder',
    },
    // Coming Soon section
    {
      label: 'COMING SOON',
      path: '#coming-soon',
      sectionHeader: true,
    },
    {
      label: 'Reference Checks',
      path: '/reference-checks',
      icon: PhoneIcon,
      comingSoon: true,
      pageKey: 'reference-checks',
    },
  ],
};

/* ============================================
   Workspace Section
   ============================================ */

export const workspacesConfig: SectionConfig = {
  id: 'workspaces',
  title: 'Workspace',
  icon: FolderIcon,
  basePath: '/workspaces/:workspaceId',
  items: [
    {
      label: 'Overview',
      path: '/workspaces/:workspaceId',
      icon: EyeIcon,
      exact: true,
    },
    {
      label: 'Setup Guide',
      path: '/workspaces/:workspaceId/semantic/setup',
      icon: WrenchScrewdriverIcon,
    },
    {
      label: 'Semantic Ontology',
      path: '/workspaces/:workspaceId/semantic/ontology',
      icon: RectangleGroupIcon,
    },
    {
      label: 'Verified Queries',
      path: '/workspaces/:workspaceId/semantic/verified-queries',
      icon: ClipboardDocumentCheckIcon,
    },
    {
      label: 'Context Packs',
      path: '/workspaces/:workspaceId/semantic/context-packs',
      icon: CubeIcon,
    },
    {
      label: 'Corrections Raised',
      path: '/workspaces/:workspaceId/semantic/corrections',
      icon: ClipboardDocumentListIcon,
    },
    {
      label: 'Admin',
      path: '#workspace-admin',
      sectionHeader: true,
    },
    {
      label: 'MCP & External Access',
      path: '/workspaces/:workspaceId/semantic/mcp',
      icon: CommandLineIcon,
    },
    {
      label: 'Telemetry',
      path: '/telemetry?workspaceId=:workspaceId',
      icon: ChartBarIcon,
      outbound: true,
      pageKey: 'telemetry',
    },
    {
      label: 'Data Connections',
      path: '/data-connections?workspaceId=:workspaceId',
      icon: LinkIcon,
      outbound: true,
      pageKey: 'data-connections',
    },
    {
      label: 'Workspace Settings',
      path: '/workspaces/:workspaceId/settings',
      icon: Cog6ToothIcon,
    },
  ],
};

/* ============================================
   Chat Section (formerly Business Intelligence)
   ============================================ */

export const chatConfig: SectionConfig = {
  id: 'chat',
  title: 'Chat',
  icon: ChatBubbleLeftRightIcon,
  basePath: '/chat',
  items: [
    {
      label: 'New Chat',
      path: '/chat',
      icon: ChatBubbleLeftRightIcon,
    },
    // Conversations are shown dynamically in the sidebar
  ],
};

/* ============================================
   Review Intelligence Section
   ============================================ */

export const reviewIntelligenceConfig: SectionConfig = {
  id: 'review-intelligence',
  title: 'Review Intelligence',
  icon: StarIcon,
  basePath: '/bv-reviews',
  items: [
    {
      label: 'Studio',
      path: '/bv-reviews/studio',
      icon: SparklesIcon,
      pageKey: 'bv-reviews-studio',
      requiredPermissions: ['bv:read', 'bv:write', 'platform:admin'],
    },
    {
      label: 'Insights Chat',
      path: '/bv-reviews/insights',
      icon: ChatBubbleLeftRightIcon,
      pageKey: 'bv-reviews-insights',
      requiredPermissions: ['bv:read', 'bv:write', 'platform:admin'],
    },
    {
      label: 'Library',
      path: '/bv-reviews/library',
      icon: ClipboardDocumentListIcon,
      pageKey: 'bv-reviews-library',
      requiredPermissions: ['bv:read', 'bv:write', 'platform:admin'],
    },
    {
      label: 'Settings',
      path: '/bv-reviews/settings',
      icon: Cog6ToothIcon,
      pageKey: 'bv-reviews-settings',
      requiredPermissions: ['bv:admin', 'platform:admin'],
    },
  ],
};

/* ============================================
   Catalog Section
   ============================================ */

export const catalogConfig: SectionConfig = {
  id: 'catalog',
  title: 'Catalog',
  icon: CircleStackIcon,
  basePath: '/catalog',
  items: [
    {
      label: 'Overview',
      path: '/catalog',
      icon: EyeIcon,
      exact: true,
      pageKey: 'catalog',
      requiredPermissions: ['connections:read', 'catalog:admin', 'platform:admin'],
    },
    {
      label: 'Assets',
      path: '/catalog/assets',
      icon: CircleStackIcon,
      pageKey: 'catalog',
      requiredPermissions: ['connections:read', 'catalog:admin', 'platform:admin'],
    },
    {
      label: 'Corrections',
      path: '/catalog/corrections',
      icon: ClipboardDocumentListIcon,
      pageKey: 'catalog',
      requiredPermissions: ['connections:read', 'catalog:admin', 'platform:admin'],
    },
    {
      label: 'Providers',
      path: '/catalog/providers',
      icon: LinkIcon,
      pageKey: 'catalog',
      requiredPermissions: ['connections:read', 'catalog:admin', 'platform:admin'],
    },
    {
      label: 'Settings',
      path: '/catalog/settings',
      icon: Cog6ToothIcon,
      pageKey: 'catalog',
      requiredPermissions: ['connections:read', 'catalog:admin', 'platform:admin'],
    },
  ],
};

/* ============================================
   13-Week Cash Flow Section
   ============================================ */

export const cashflowConfig: SectionConfig = {
  id: 'cashflow',
  title: '13-Week Cash Flow',
  icon: BanknotesIcon,
  basePath: '/cashflow',
  items: [
    {
      label: 'Forecast Summary',
      path: '/cashflow',
      icon: PresentationChartBarIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Summary Dashboard',
      path: '/cashflow/ceo',
      icon: ChartBarIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Receipts',
      path: '/cashflow/receipts',
      icon: ArrowTrendingUpIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Disbursements',
      path: '/cashflow/disbursements',
      icon: ArrowTrendingDownIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Scenarios',
      path: '/cashflow/scenarios',
      icon: AdjustmentsVerticalIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Scenario Modeling',
      path: '/cashflow/scenario-modeling',
      icon: PresentationChartBarIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Risk Register',
      path: '/cashflow/risk-register',
      icon: ExclamationTriangleIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Variance',
      path: '/cashflow/variance',
      icon: ArrowsRightLeftIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Cash Availability',
      path: '/cashflow/availability',
      icon: ShieldCheckIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Variance Intelligence',
      path: '/cashflow/variance-intelligence',
      icon: SparklesIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Monthly Rollup',
      path: '/cashflow/monthly-rollup',
      icon: ClockIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Budget Engine',
      path: '/cashflow/budget-engine',
      icon: ClipboardDocumentListIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'FP&A Feedback',
      path: '/cashflow/fpa-feedback',
      icon: ArrowsRightLeftIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Working Capital',
      path: '/cashflow/working-capital',
      icon: BanknotesIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Drift Monitor',
      path: '/cashflow/drift-monitor',
      icon: CpuChipIcon,
      pageKey: 'cashflow',
    },
    {
      label: 'Configuration',
      path: '/cashflow/configuration',
      icon: WrenchScrewdriverIcon,
      pageKey: 'cashflow',
    },
  ],
};

export const cashflowV2Config: SectionConfig = {
  id: 'cashflow-v2',
  title: '13-Week Cash Flow V2',
  icon: BanknotesIcon,
  basePath: '/cashflow-v2',
  items: [
    {
      label: 'Liquidity Pulse',
      path: '/cashflow-v2',
      icon: PresentationChartBarIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'Cash Truth',
      path: '/cashflow-v2/liquidity/cash-truth',
      icon: ShieldCheckIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'Drift Monitor',
      path: '/cashflow-v2/liquidity/drift',
      icon: CpuChipIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: '13-Week Grid',
      path: '/cashflow-v2/forecast/grid',
      icon: PresentationChartBarIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'Variance Intelligence',
      path: '/cashflow-v2/forecast/variance',
      icon: SparklesIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'Entity Scorecard',
      path: '/cashflow-v2/portfolio/entities',
      icon: BuildingOffice2Icon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'Acquisition Simulator',
      path: '/cashflow-v2/portfolio/acquisition',
      icon: AdjustmentsVerticalIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'AR Collections',
      path: '/cashflow-v2/working-capital/collections',
      icon: ArrowTrendingUpIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'WC Recommendations',
      path: '/cashflow-v2/working-capital/recommendations',
      icon: BanknotesIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'Board & Lender Export',
      path: '/cashflow-v2/reporting/exports',
      icon: ClipboardDocumentListIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'AI Operations',
      path: '/cashflow-v2/admin/ai-ops',
      icon: CommandLineIcon,
      pageKey: 'cashflow_v2',
    },
    {
      label: 'Configuration',
      path: '/cashflow-v2/admin/configuration',
      icon: WrenchScrewdriverIcon,
      pageKey: 'cashflow_v2',
    },
  ],
};

export const bankReconciliationConfig: SectionConfig = {
  id: 'bank-reconciliation',
  title: 'Bank Reconciliation',
  icon: ScaleIcon,
  basePath: '/bank-reconciliation',
  items: [
    {
      label: 'Decision Console',
      path: '/bank-reconciliation',
      icon: ScaleIcon,
      pageKey: 'bank_reconciliation',
      requiredPermissions: ['bank_reconciliation:read', 'platform:admin'],
    },
  ],
};

export const closeCalendarConfig: SectionConfig = {
  id: 'close-calendar',
  title: 'Close Calendar',
  icon: ClockIcon,
  basePath: '/close-calendar',
  items: [
    {
      label: 'Calendar & Gates',
      path: '/close-calendar',
      icon: ClockIcon,
      pageKey: 'close_calendar',
      requiredPermissions: ['close_calendar:read', 'platform:admin'],
    },
    {
      label: 'Rules Engine',
      path: '/close-calendar/rules',
      icon: ShieldCheckIcon,
      pageKey: 'close_calendar_rules',
      requiredPermissions: ['close_calendar:read', 'platform:admin'],
    },
  ],
};

export const judgmentalAccrualsConfig: SectionConfig = {
  id: 'judgmental-accruals',
  title: 'Judgmental Accruals',
  icon: ScaleIcon,
  basePath: '/judgmental-accruals',
  items: [
    {
      label: 'Workpaper',
      path: '/judgmental-accruals',
      icon: ClipboardDocumentListIcon,
      pageKey: 'judgmental-accruals',
      requiredPermissions: ['judgmental_accruals:read', 'platform:admin'],
    },
    {
      label: 'Source Log',
      path: '/judgmental-accruals/source-log',
      icon: TableCellsIcon,
      pageKey: 'judgmental-accruals',
      requiredPermissions: ['judgmental_accruals:read', 'platform:admin'],
    },
    {
      label: 'Instructions',
      path: '/judgmental-accruals/instructions',
      icon: DocumentTextIcon,
      pageKey: 'judgmental-accruals',
      requiredPermissions: ['judgmental_accruals:read', 'platform:admin'],
    },
    {
      label: 'Configuration',
      path: '/judgmental-accruals/config',
      icon: WrenchScrewdriverIcon,
      pageKey: 'judgmental-accruals',
      requiredPermissions: ['judgmental_accruals:write', 'platform:admin'],
    },
  ],
};

export const lagAccrualsConfig: SectionConfig = {
  id: 'lag-accruals',
  title: 'Lag Accruals',
  icon: ClipboardDocumentListIcon,
  basePath: '/lag-accruals',
  items: [
    {
      label: 'Workbench',
      path: '/lag-accruals',
      icon: ClipboardDocumentListIcon,
      pageKey: 'lag_accruals',
      requiredPermissions: ['cashflow:read', 'platform:admin'],
    },
    {
      label: 'HITL Review',
      path: '/lag-accruals/hitl',
      icon: ExclamationTriangleIcon,
      pageKey: 'lag_accruals',
      requiredPermissions: ['cashflow:write', 'platform:admin'],
    },
    {
      label: 'NetSuite Exports',
      path: '/lag-accruals/exports',
      icon: DocumentTextIcon,
      pageKey: 'lag_accruals',
      requiredPermissions: ['cashflow:export', 'platform:admin'],
    },
    {
      label: 'Runbook',
      path: '/lag-accruals/runbook',
      icon: DocumentTextIcon,
      pageKey: 'lag_accruals',
      requiredPermissions: ['cashflow:read', 'platform:admin'],
    },
    {
      label: 'Source Report',
      path: '/lag-accruals/source-report',
      icon: TableCellsIcon,
      pageKey: 'lag_accruals',
      requiredPermissions: ['cashflow:read', 'platform:admin'],
    },
    {
      label: 'Data Source',
      path: '/lag-accruals/admin/configuration',
      icon: ServerStackIcon,
      pageKey: 'lag_accruals',
      requiredPermissions: ['cashflow:admin', 'platform:admin'],
    },
  ],
};

/* ============================================
   Payroll Entry Section
   ============================================ */

export const payrollEntryConfig: SectionConfig = {
  id: 'payroll-entry',
  title: 'Payroll Accrual',
  icon: ClipboardDocumentListIcon,
  basePath: '/payroll-entry',
  items: [
    {
      label: 'Controller Review',
      path: '/payroll-entry',
      icon: ClipboardDocumentListIcon,
      pageKey: 'payroll-entry',
    },
    {
      label: 'Journal Entry',
      path: '/payroll-entry/journal-entry',
      icon: DocumentTextIcon,
      pageKey: 'payroll-entry',
    },
    {
      label: 'Headcount Tie-Out',
      path: '/payroll-entry/headcount',
      icon: UserGroupIcon,
      pageKey: 'payroll-entry',
    },
    {
      label: 'Burden Detail',
      path: '/payroll-entry/burden',
      icon: BanknotesIcon,
      pageKey: 'payroll-entry',
    },
    {
      label: 'Audit Log',
      path: '/payroll-entry/audit',
      icon: ClockIcon,
      pageKey: 'payroll-entry',
    },
    {
      label: 'Source Files',
      path: '/payroll-entry/source-files',
      icon: LinkIcon,
      pageKey: 'payroll-entry',
    },
    {
      label: 'Configuration',
      path: '/payroll-entry/configuration',
      icon: WrenchScrewdriverIcon,
      pageKey: 'payroll-entry',
    },
  ],
};

/* ============================================
   AP Cut-Off Enforcer Section
   ============================================ */

export const apCutoffEnforcerConfig: SectionConfig = {
  id: 'ap-cutoff-enforcer',
  title: 'AP Cut-Off Enforcer',
  icon: ClipboardDocumentListIcon,
  basePath: '/ap-cutoff-enforcer',
  items: [
    {
      label: 'Controller Review',
      path: '/ap-cutoff-enforcer',
      icon: ClipboardDocumentListIcon,
      pageKey: 'ap-cutoff-enforcer',
      requiredPermissions: ['ap_cutoff:read', 'platform:admin'],
    },
    {
      label: 'Exceptions',
      path: '/ap-cutoff-enforcer/exceptions',
      icon: ExclamationTriangleIcon,
      pageKey: 'ap-cutoff-enforcer',
      requiredPermissions: ['ap_cutoff:read', 'platform:admin'],
    },
    {
      label: 'Journal Entry',
      path: '/ap-cutoff-enforcer/journal-entry',
      icon: DocumentTextIcon,
      pageKey: 'ap-cutoff-enforcer',
      requiredPermissions: ['ap_cutoff:read', 'platform:admin'],
    },
    {
      label: 'Source Data',
      path: '/ap-cutoff-enforcer/source-data',
      icon: TableCellsIcon,
      pageKey: 'ap-cutoff-enforcer',
      requiredPermissions: ['ap_cutoff:read', 'platform:admin'],
    },
    {
      label: 'Configuration',
      path: '/ap-cutoff-enforcer/configuration',
      icon: WrenchScrewdriverIcon,
      pageKey: 'ap-cutoff-enforcer',
      requiredPermissions: ['ap_cutoff:admin', 'platform:admin'],
    },
  ],
};

/* ============================================
   Variable Lag Accruals Section
   ============================================ */

export const variableLagAccrualsConfig: SectionConfig = {
  id: 'variable-lag-accruals',
  title: 'Variable Lag Accruals',
  icon: ClipboardDocumentListIcon,
  basePath: '/variable-lag-accruals',
  items: [
    {
      label: 'Close Review',
      path: '/variable-lag-accruals',
      icon: PresentationChartBarIcon,
      pageKey: 'variable-lag-accruals',
    },
    {
      label: 'HITL Review',
      path: '/variable-lag-accruals/review',
      icon: ShieldCheckIcon,
      pageKey: 'variable-lag-accruals',
    },
    {
      label: 'Source Evidence',
      path: '/variable-lag-accruals/sources',
      icon: LinkIcon,
      pageKey: 'variable-lag-accruals',
    },
    {
      label: 'NetSuite Exports',
      path: '/variable-lag-accruals/exports',
      icon: DocumentTextIcon,
      pageKey: 'variable-lag-accruals',
    },
    {
      label: 'Configuration',
      path: '/variable-lag-accruals/configuration',
      icon: WrenchScrewdriverIcon,
      pageKey: 'variable-lag-accruals',
    },
  ],
};

/* ============================================
   Cloud Accruals Section
   ============================================ */

export const cloudAccrualsConfig: SectionConfig = {
  id: 'cloud-accruals',
  title: 'Cloud Accruals',
  icon: ServerStackIcon,
  basePath: '/cloud-accruals',
  items: [
    {
      label: 'Close Review',
      path: '/cloud-accruals',
      icon: PresentationChartBarIcon,
      pageKey: 'cloud-accruals',
    },
    {
      label: 'Controller Review',
      path: '/cloud-accruals/review',
      icon: ShieldCheckIcon,
      pageKey: 'cloud-accruals',
    },
    {
      label: 'Source Evidence',
      path: '/cloud-accruals/sources',
      icon: LinkIcon,
      pageKey: 'cloud-accruals',
    },
    {
      label: 'NetSuite Exports',
      path: '/cloud-accruals/exports',
      icon: DocumentTextIcon,
      pageKey: 'cloud-accruals',
    },
    {
      label: 'Configuration',
      path: '/cloud-accruals/configuration',
      icon: WrenchScrewdriverIcon,
      pageKey: 'cloud-accruals',
    },
  ],
};

/* ============================================
   ASC 606 Revenue Waterfall Section
   ============================================ */

export const asc606RevenueWaterfallConfig: SectionConfig = {
  id: 'asc-606-revenue-waterfall',
  title: 'ASC 606 Revenue Waterfall',
  icon: BanknotesIcon,
  basePath: '/asc-606-revenue-waterfall',
  items: [
    {
      label: 'Close Review',
      path: '/asc-606-revenue-waterfall',
      icon: PresentationChartBarIcon,
      pageKey: 'asc-606-revenue-waterfall',
    },
    {
      label: 'Contract Review',
      path: '/asc-606-revenue-waterfall/review',
      icon: ShieldCheckIcon,
      pageKey: 'asc-606-revenue-waterfall',
    },
    {
      label: 'Source Evidence',
      path: '/asc-606-revenue-waterfall/sources',
      icon: LinkIcon,
      pageKey: 'asc-606-revenue-waterfall',
    },
    {
      label: 'NetSuite Exports',
      path: '/asc-606-revenue-waterfall/exports',
      icon: DocumentTextIcon,
      pageKey: 'asc-606-revenue-waterfall',
    },
  ],
};

/* ============================================
   Deferred Revenue Scheduler Section
   ============================================ */

export const deferredRevenueSchedulerConfig: SectionConfig = {
  id: 'deferred-revenue-scheduler',
  title: 'Deferred Revenue Scheduler',
  icon: DocumentCheckIcon,
  basePath: '/deferred-revenue-scheduler',
  items: [
    {
      label: 'Roll Forward',
      path: '/deferred-revenue-scheduler',
      icon: TableCellsIcon,
      pageKey: 'deferred-revenue-scheduler',
    },
    {
      label: 'Gate 2 Review',
      path: '/deferred-revenue-scheduler/review',
      icon: ShieldCheckIcon,
      pageKey: 'deferred-revenue-scheduler',
    },
    {
      label: 'Source Evidence',
      path: '/deferred-revenue-scheduler/sources',
      icon: LinkIcon,
      pageKey: 'deferred-revenue-scheduler',
    },
    {
      label: 'NetSuite Exports',
      path: '/deferred-revenue-scheduler/exports',
      icon: DocumentTextIcon,
      pageKey: 'deferred-revenue-scheduler',
    },
  ],
};

/* ============================================
   Sub-Ledger Revenue Reconciler Section
   ============================================ */

export const subledgerRevenueReconcilerConfig: SectionConfig = {
  id: 'subledger-revenue-reconciler',
  title: 'Sub-Ledger Revenue Reconciler',
  icon: BanknotesIcon,
  basePath: '/subledger-revenue-reconciler',
  items: [
    {
      label: 'Close Review',
      path: '/subledger-revenue-reconciler',
      icon: PresentationChartBarIcon,
      pageKey: 'subledger-revenue-reconciler',
    },
    {
      label: 'Reconciling Items',
      path: '/subledger-revenue-reconciler/review',
      icon: ShieldCheckIcon,
      pageKey: 'subledger-revenue-reconciler',
    },
    {
      label: 'Source Evidence',
      path: '/subledger-revenue-reconciler/sources',
      icon: LinkIcon,
      pageKey: 'subledger-revenue-reconciler',
    },
    {
      label: 'NetSuite Exports',
      path: '/subledger-revenue-reconciler/exports',
      icon: DocumentTextIcon,
      pageKey: 'subledger-revenue-reconciler',
    },
  ],
};

/* ============================================
   Admin Settings Section (Tenant Admin)
   ============================================ */

export const adminSettingsConfig: SectionConfig = {
  id: 'admin-settings',
  title: 'Admin Settings',
  icon: Cog6ToothIcon,
  basePath: '/admin',
  items: [
    {
      label: 'Data Connections',
      path: '/data-connections',
      icon: LinkIcon,
      pageKey: 'data-connections',
    },
    {
      label: 'Users & Roles',
      path: '/tenant-admin/users',
      icon: UsersIcon,
    },
    {
      label: 'AI Providers',
      path: '/admin/settings',
      icon: CpuChipIcon,
    },
    {
      label: 'Adoption Settings',
      path: '/admin/adoption-settings',
      icon: ChartBarIcon,
      pageKey: 'adoption',
    },
    {
      label: 'Job Scheduler',
      path: '/admin/jobs',
      icon: ClockIcon,
      requiredPermissions: ['adoption:manage_jobs', 'platform:admin'],
      pageKey: 'adoption',
    },
    {
      label: 'Theme',
      path: '/tenant-admin/theme',
      icon: SwatchIcon,
    },
    {
      label: 'Email',
      path: '/tenant-admin/email',
      icon: EnvelopeIcon,
      requiredPermissions: ['admin:settings:read', 'admin:settings:update', 'platform:admin'],
    },
    {
      label: 'SSO',
      path: '/tenant-admin/sso',
      icon: ShieldCheckIcon,
      // Intentionally ungated by applet page keys: SSO is platform core.
    },
    {
      label: 'Outbound Access (MCP)',
      path: '/admin/mcp-servers',
      icon: ServerStackIcon,
    },
  ],
};

/* ============================================
   AI Console Section
   MLOps tools: Evals, GEPA Optimizer, Prompt Management
   ============================================ */

export const aiConsoleConfig: SectionConfig = {
  id: 'ai-console',
  title: 'AI Console',
  icon: CommandLineIcon,
  basePath: '/ai-console',
  items: [
    {
      label: 'Dashboard',
      path: '/ai-console/dashboard',
      icon: PresentationChartBarIcon,
      requiredPermissions: ['ai_console:access'],
      pageKey: 'ai-console-dashboard',
    },
    {
      label: 'Agent Lens',
      path: '/ai-console/agent-lens',
      icon: EyeIcon,
      requiredPermissions: ['ai_console:access'],
      pageKey: 'ai-console-agent-lens',
    },
    {
      label: 'Agent Testing',
      path: '/ai-console/agent-testing',
      icon: PlayIcon,
      requiredPermissions: ['ai_console:sandbox'],
      pageKey: 'ai-console-agent-testing',
    },
    {
      label: 'RAG Evaluations',
      path: '/evals',
      icon: BeakerIcon,
      requiredPermissions: ['ai_console:access', 'platform:admin'],
      pageKey: 'evals',
    },
    {
      label: 'Telemetry',
      path: '/telemetry',
      icon: ChartBarIcon,
      requiredPermissions: ['ai_console:access', 'platform:admin'],
      pageKey: 'telemetry',
    },
    {
      label: 'GEPA Optimizer',
      path: '/gepa-optimizer',
      icon: SparklesIcon,
      requiredPermissions: ['ai_console:access', 'platform:admin'],
      pageKey: 'gepa-optimizer',
    },
    {
      label: 'Prompt Management',
      path: '/prompt-management',
      icon: AdjustmentsHorizontalIcon,
      requiredPermissions: ['ai_console:access', 'platform:admin'],
      pageKey: 'prompt-management',
    },
    {
      label: 'Tiny Model Studio',
      path: '/ai-console/tiny-model-studio',
      icon: CpuChipIcon,
      requiredPermissions: ['ai_console:access', 'platform:admin'],
      pageKey: 'tiny-model-studio',
    },
  ],
};

/* ============================================
   Platform Settings Section (Platform Admin)
   Includes Tenant Management items
   ============================================ */

export const platformSettingsConfig: SectionConfig = {
  id: 'platform-settings',
  title: 'Platform Settings',
  icon: BuildingOffice2Icon,
  basePath: '/platform-admin',
  items: [
    {
      label: 'Admin Management',
      path: '/platform-admin/admins',
      icon: UsersIcon,
    },
    {
      label: 'AI Providers',
      path: '/platform-admin/providers',
      icon: CpuChipIcon,
    },
    {
      label: 'Platform Email',
      path: '/platform-admin/email',
      icon: EnvelopeIcon,
      requiredPermissions: ['system:admin'],
    },
    {
      label: 'Job Scheduler',
      path: '/platform-admin/jobs',
      icon: ClockIcon,
    },
    {
      label: 'SSO Policy',
      path: '/platform-admin/sso-policy',
      icon: ShieldCheckIcon,
      // Intentionally ungated by applet page keys: SSO is platform core.
    },
    {
      label: 'Tenant Management',
      path: '/platform-admin/tenants',
      icon: BuildingOffice2Icon,
    },
    {
      label: 'Feature Allocation',
      path: '/platform-admin/features',
      icon: ShieldCheckIcon,
    },
    {
      label: 'Adoption Access',
      path: '/platform-admin/adoption',
      icon: ChartBarIcon,
      pageKey: 'adoption',
    },
  ],
};

/* ============================================
   Monthly Close Hub Section
   Aggregates all close applets into one persistent section nav.
   ============================================ */

/** Base paths for all monthly close applets — used for path detection */
export const MONTHLY_CLOSE_PATHS = [
  '/close-calendar',
  '/bank-reconciliation',
  '/payroll-entry',
  '/ap-cutoff-enforcer',
  '/judgmental-accruals',
  '/lag-accruals',
  '/variable-lag-accruals',
  '/cloud-accruals',
  '/asc-606-revenue-waterfall',
  '/deferred-revenue-scheduler',
  '/subledger-revenue-reconciler',
] as const;

export const monthlyCloseConfig: SectionConfig = {
  id: 'monthly-close',
  title: 'Monthly Close',
  icon: CalendarDaysIcon,
  basePath: '/close-calendar',
  items: [
    {
      isGroup: true,
      id: 'close-calendar',
      label: 'Close Calendar',
      icon: ClockIcon,
      basePath: '/close-calendar',
      defaultPath: '/close-calendar',
      pageKey: 'close_calendar',
      requiredPermissions: ['close_calendar:read', 'platform:admin'],
      items: [
        { label: 'Calendar & Gates', path: '/close-calendar', icon: ClockIcon, pageKey: 'close_calendar', requiredPermissions: ['close_calendar:read', 'platform:admin'] },
        { label: 'Rules Engine', path: '/close-calendar/rules', icon: ShieldCheckIcon, pageKey: 'close_calendar_rules', requiredPermissions: ['close_calendar:read', 'platform:admin'] },
      ],
    },
    {
      isGroup: true,
      id: 'bank-reconciliation',
      label: 'Bank Reconciliation',
      icon: ScaleIcon,
      basePath: '/bank-reconciliation',
      defaultPath: '/bank-reconciliation',
      pageKey: 'bank_reconciliation',
      requiredPermissions: ['bank_reconciliation:read', 'platform:admin'],
      items: [
        { label: 'Decision Console', path: '/bank-reconciliation', icon: ScaleIcon, pageKey: 'bank_reconciliation', requiredPermissions: ['bank_reconciliation:read', 'platform:admin'] },
      ],
    },
    {
      isGroup: true,
      id: 'payroll-entry',
      label: 'Payroll Accrual',
      icon: ClipboardDocumentListIcon,
      basePath: '/payroll-entry',
      defaultPath: '/payroll-entry',
      pageKey: 'payroll-entry',
      items: [
        { label: 'Controller Review', path: '/payroll-entry', icon: ClipboardDocumentListIcon, pageKey: 'payroll-entry' },
        { label: 'Journal Entry', path: '/payroll-entry/journal-entry', icon: DocumentTextIcon, pageKey: 'payroll-entry' },
        { label: 'Headcount Tie-Out', path: '/payroll-entry/headcount', icon: UserGroupIcon, pageKey: 'payroll-entry' },
        { label: 'Burden Detail', path: '/payroll-entry/burden', icon: BanknotesIcon, pageKey: 'payroll-entry' },
        { label: 'Audit Log', path: '/payroll-entry/audit', icon: ClockIcon, pageKey: 'payroll-entry' },
        { label: 'Source Files', path: '/payroll-entry/source-files', icon: LinkIcon, pageKey: 'payroll-entry' },
        { label: 'Configuration', path: '/payroll-entry/configuration', icon: WrenchScrewdriverIcon, pageKey: 'payroll-entry' },
      ],
    },
    {
      isGroup: true,
      id: 'ap-cutoff-enforcer',
      label: 'AP Cut-Off Enforcer',
      icon: ClipboardDocumentListIcon,
      basePath: '/ap-cutoff-enforcer',
      defaultPath: '/ap-cutoff-enforcer',
      pageKey: 'ap-cutoff-enforcer',
      requiredPermissions: ['ap_cutoff:read', 'platform:admin'],
      items: [
        { label: 'Controller Review', path: '/ap-cutoff-enforcer', icon: ClipboardDocumentListIcon, pageKey: 'ap-cutoff-enforcer', requiredPermissions: ['ap_cutoff:read', 'platform:admin'] },
        { label: 'Exceptions', path: '/ap-cutoff-enforcer/exceptions', icon: ExclamationTriangleIcon, pageKey: 'ap-cutoff-enforcer', requiredPermissions: ['ap_cutoff:read', 'platform:admin'] },
        { label: 'Journal Entry', path: '/ap-cutoff-enforcer/journal-entry', icon: DocumentTextIcon, pageKey: 'ap-cutoff-enforcer', requiredPermissions: ['ap_cutoff:read', 'platform:admin'] },
        { label: 'Source Data', path: '/ap-cutoff-enforcer/source-data', icon: TableCellsIcon, pageKey: 'ap-cutoff-enforcer', requiredPermissions: ['ap_cutoff:read', 'platform:admin'] },
        { label: 'Configuration', path: '/ap-cutoff-enforcer/configuration', icon: WrenchScrewdriverIcon, pageKey: 'ap-cutoff-enforcer', requiredPermissions: ['ap_cutoff:admin', 'platform:admin'] },
      ],
    },
    {
      isGroup: true,
      id: 'judgmental-accruals',
      label: 'Judgmental Accruals',
      icon: ScaleIcon,
      basePath: '/judgmental-accruals',
      defaultPath: '/judgmental-accruals',
      pageKey: 'judgmental-accruals',
      requiredPermissions: ['judgmental_accruals:read', 'platform:admin'],
      items: [
        { label: 'Workpaper', path: '/judgmental-accruals', icon: ClipboardDocumentListIcon, pageKey: 'judgmental-accruals', requiredPermissions: ['judgmental_accruals:read', 'platform:admin'] },
        { label: 'Source Log', path: '/judgmental-accruals/source-log', icon: TableCellsIcon, pageKey: 'judgmental-accruals', requiredPermissions: ['judgmental_accruals:read', 'platform:admin'] },
        { label: 'Instructions', path: '/judgmental-accruals/instructions', icon: DocumentTextIcon, pageKey: 'judgmental-accruals', requiredPermissions: ['judgmental_accruals:read', 'platform:admin'] },
        { label: 'Configuration', path: '/judgmental-accruals/config', icon: WrenchScrewdriverIcon, pageKey: 'judgmental-accruals', requiredPermissions: ['judgmental_accruals:write', 'platform:admin'] },
      ],
    },
    {
      isGroup: true,
      id: 'lag-accruals',
      label: 'Lag Accruals',
      icon: ClipboardDocumentListIcon,
      basePath: '/lag-accruals',
      defaultPath: '/lag-accruals',
      pageKey: 'lag_accruals',
      requiredPermissions: ['cashflow:read', 'platform:admin'],
      items: [
        { label: 'Workbench', path: '/lag-accruals', icon: ClipboardDocumentListIcon, pageKey: 'lag_accruals', requiredPermissions: ['cashflow:read', 'platform:admin'] },
        { label: 'HITL Review', path: '/lag-accruals/hitl', icon: ExclamationTriangleIcon, pageKey: 'lag_accruals', requiredPermissions: ['cashflow:write', 'platform:admin'] },
        { label: 'NetSuite Exports', path: '/lag-accruals/exports', icon: DocumentTextIcon, pageKey: 'lag_accruals', requiredPermissions: ['cashflow:export', 'platform:admin'] },
        { label: 'Runbook', path: '/lag-accruals/runbook', icon: DocumentTextIcon, pageKey: 'lag_accruals', requiredPermissions: ['cashflow:read', 'platform:admin'] },
        { label: 'Source Report', path: '/lag-accruals/source-report', icon: TableCellsIcon, pageKey: 'lag_accruals', requiredPermissions: ['cashflow:read', 'platform:admin'] },
        { label: 'Data Source', path: '/lag-accruals/admin/configuration', icon: ServerStackIcon, pageKey: 'lag_accruals', requiredPermissions: ['cashflow:admin', 'platform:admin'] },
      ],
    },
    {
      isGroup: true,
      id: 'variable-lag-accruals',
      label: 'Variable Lag Accruals',
      icon: ClipboardDocumentListIcon,
      basePath: '/variable-lag-accruals',
      defaultPath: '/variable-lag-accruals',
      pageKey: 'variable-lag-accruals',
      items: [
        { label: 'Close Review', path: '/variable-lag-accruals', icon: PresentationChartBarIcon, pageKey: 'variable-lag-accruals' },
        { label: 'HITL Review', path: '/variable-lag-accruals/review', icon: ShieldCheckIcon, pageKey: 'variable-lag-accruals' },
        { label: 'Source Evidence', path: '/variable-lag-accruals/sources', icon: LinkIcon, pageKey: 'variable-lag-accruals' },
        { label: 'NetSuite Exports', path: '/variable-lag-accruals/exports', icon: DocumentTextIcon, pageKey: 'variable-lag-accruals' },
        { label: 'Configuration', path: '/variable-lag-accruals/configuration', icon: WrenchScrewdriverIcon, pageKey: 'variable-lag-accruals' },
      ],
    },
    {
      isGroup: true,
      id: 'cloud-accruals',
      label: 'Cloud Accruals',
      icon: CircleStackIcon,
      basePath: '/cloud-accruals',
      defaultPath: '/cloud-accruals',
      pageKey: 'cloud-accruals',
      items: [
        { label: 'Close Review', path: '/cloud-accruals', icon: PresentationChartBarIcon, pageKey: 'cloud-accruals' },
        { label: 'Controller Review', path: '/cloud-accruals/review', icon: ShieldCheckIcon, pageKey: 'cloud-accruals' },
        { label: 'Source Evidence', path: '/cloud-accruals/sources', icon: LinkIcon, pageKey: 'cloud-accruals' },
        { label: 'NetSuite Exports', path: '/cloud-accruals/exports', icon: DocumentTextIcon, pageKey: 'cloud-accruals' },
        { label: 'Configuration', path: '/cloud-accruals/configuration', icon: WrenchScrewdriverIcon, pageKey: 'cloud-accruals' },
      ],
    },
    {
      isGroup: true,
      id: 'asc-606-revenue-waterfall',
      label: 'ASC 606 Revenue',
      icon: BanknotesIcon,
      basePath: '/asc-606-revenue-waterfall',
      defaultPath: '/asc-606-revenue-waterfall',
      pageKey: 'asc-606-revenue-waterfall',
      items: [
        { label: 'Close Review', path: '/asc-606-revenue-waterfall', icon: PresentationChartBarIcon, pageKey: 'asc-606-revenue-waterfall' },
        { label: 'Contract Review', path: '/asc-606-revenue-waterfall/review', icon: ShieldCheckIcon, pageKey: 'asc-606-revenue-waterfall' },
        { label: 'Source Evidence', path: '/asc-606-revenue-waterfall/sources', icon: LinkIcon, pageKey: 'asc-606-revenue-waterfall' },
        { label: 'NetSuite Exports', path: '/asc-606-revenue-waterfall/exports', icon: DocumentTextIcon, pageKey: 'asc-606-revenue-waterfall' },
      ],
    },
    {
      isGroup: true,
      id: 'deferred-revenue-scheduler',
      label: 'Deferred Revenue',
      icon: DocumentCheckIcon,
      basePath: '/deferred-revenue-scheduler',
      defaultPath: '/deferred-revenue-scheduler',
      pageKey: 'deferred-revenue-scheduler',
      items: [
        { label: 'Roll Forward', path: '/deferred-revenue-scheduler', icon: TableCellsIcon, pageKey: 'deferred-revenue-scheduler' },
        { label: 'Gate 2 Review', path: '/deferred-revenue-scheduler/review', icon: ShieldCheckIcon, pageKey: 'deferred-revenue-scheduler' },
        { label: 'Source Evidence', path: '/deferred-revenue-scheduler/sources', icon: LinkIcon, pageKey: 'deferred-revenue-scheduler' },
        { label: 'NetSuite Exports', path: '/deferred-revenue-scheduler/exports', icon: DocumentTextIcon, pageKey: 'deferred-revenue-scheduler' },
      ],
    },
    {
      isGroup: true,
      id: 'subledger-revenue-reconciler',
      label: 'Sub-Ledger Reconciler',
      icon: BanknotesIcon,
      basePath: '/subledger-revenue-reconciler',
      defaultPath: '/subledger-revenue-reconciler',
      pageKey: 'subledger-revenue-reconciler',
      items: [
        { label: 'Close Review', path: '/subledger-revenue-reconciler', icon: PresentationChartBarIcon, pageKey: 'subledger-revenue-reconciler' },
        { label: 'Reconciling Items', path: '/subledger-revenue-reconciler/review', icon: ShieldCheckIcon, pageKey: 'subledger-revenue-reconciler' },
        { label: 'Source Evidence', path: '/subledger-revenue-reconciler/sources', icon: LinkIcon, pageKey: 'subledger-revenue-reconciler' },
        { label: 'NetSuite Exports', path: '/subledger-revenue-reconciler/exports', icon: DocumentTextIcon, pageKey: 'subledger-revenue-reconciler' },
      ],
    },
  ],
};

/* ============================================
   All Sections Map
   ============================================ */

export const sectionConfigs: Record<NonNullable<ActiveSection>, SectionConfig> = {
  'ai-recruiter': aiRecruiterConfig,
  'workspaces': workspacesConfig,
  'chat': chatConfig,
  'review-intelligence': reviewIntelligenceConfig,
  'catalog': catalogConfig,
  'cashflow': cashflowConfig,
  'cashflow-v2': cashflowV2Config,
  'monthly-close': monthlyCloseConfig,
  'bank-reconciliation': bankReconciliationConfig,
  'close-calendar': closeCalendarConfig,
  'payroll-entry': payrollEntryConfig,
  'ap-cutoff-enforcer': apCutoffEnforcerConfig,
  'judgmental-accruals': judgmentalAccrualsConfig,
  'lag-accruals': lagAccrualsConfig,
  'variable-lag-accruals': variableLagAccrualsConfig,
  'asc-606-revenue-waterfall': asc606RevenueWaterfallConfig,
  'deferred-revenue-scheduler': deferredRevenueSchedulerConfig,
  'subledger-revenue-reconciler': subledgerRevenueReconcilerConfig,
  'cloud-accruals': cloudAccrualsConfig,
  'ai-console': aiConsoleConfig,
  'admin-settings': adminSettingsConfig,
  'platform-settings': platformSettingsConfig,
};

/* ============================================
   Helper Functions
   ============================================ */

/**
 * Get section config by ID
 */
export function getSectionConfig(sectionId: ActiveSection): SectionConfig | null {
  if (!sectionId) return null;
  return sectionConfigs[sectionId] || null;
}

function pathMatchesItem(itemPath: string, currentPath: string): boolean {
  if (itemPath.includes(':')) {
    const pattern = itemPath
      .split('/')
      .map((part) => (part.startsWith(':') ? '[^/]+' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
      .join('/');
    return new RegExp(`^${pattern}(?:/.*)?$`).test(currentPath);
  }
  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
}

function pathMatchesExactItem(itemPath: string, currentPath: string): boolean {
  if (itemPath.includes(':')) {
    const pattern = itemPath
      .split('/')
      .map((part) => (part.startsWith(':') ? '[^/]+' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
      .join('/');
    return new RegExp(`^${pattern}$`).test(currentPath);
  }
  return currentPath === itemPath;
}

function pathMatchesBase(basePath: string, currentPath: string): boolean {
  if (basePath.includes(':')) {
    const pattern = basePath
      .split('/')
      .map((part) => (part.startsWith(':') ? '[^/]+' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
      .join('/');
    return new RegExp(`^${pattern}(?:/.*)?$`).test(currentPath);
  }
  return currentPath === basePath || currentPath.startsWith(`${basePath}/`);
}

function isItemPathEnabled(item: SectionNavItem): boolean {
  if (!item.path || item.path.startsWith('#')) return false;
  if (item.sectionHeader || item.comingSoon || item.disabled) return false;
  if (item.pageKey && !isFrontendPageEnabled(item.pageKey)) return false;
  return true;
}

export function isSectionItemPathEnabled(path: string): boolean {
  for (const config of Object.values(sectionConfigs)) {
    for (const item of config.items) {
      if ('isGroup' in item) {
        for (const subItem of item.items) {
          if (!subItem.path || subItem.path.startsWith('#')) continue;
          const matches = subItem.exact
            ? pathMatchesExactItem(subItem.path, path)
            : pathMatchesItem(subItem.path, path);
          if (matches) return isItemPathEnabled(subItem);
        }
        continue;
      }
      if (!item.path || item.path.startsWith('#')) continue;
      const matches = item.exact
        ? pathMatchesExactItem(item.path, path)
        : pathMatchesItem(item.path, path);
      if (matches) return isItemPathEnabled(item);
    }
  }
  return false;
}

/**
 * Get section ID from current path
 */
export function getSectionFromPath(path: string): ActiveSection {
  // Legacy BI paths remain mapped to chat section.
  if (path.startsWith('/data-analyst') || path.startsWith('/business-intelligence')) {
    return 'chat';
  }

  const NON_ADMIN_CONTEXT_PATHS = [
    '/admin/catalog/corrections',
    '/admin/ontology',
    '/admin/context-packs',
  ];
  if (NON_ADMIN_CONTEXT_PATHS.some(p => path === p || path.startsWith(`${p}/`))) {
    return null;
  }

  // Monthly close paths always map to the unified monthly-close section.
  if (MONTHLY_CLOSE_PATHS.some(p => path === p || path.startsWith(`${p}/`))) {
    return 'monthly-close';
  }

  // Prefer explicit submenu item matches with applet/page-aware enablement.
  // Skip individual monthly-close section configs to avoid conflicting detection.
  const MONTHLY_CLOSE_SECTION_IDS = new Set([
    'bank-reconciliation', 'close-calendar', 'payroll-entry', 'ap-cutoff-enforcer',
    'judgmental-accruals', 'lag-accruals', 'variable-lag-accruals', 'cloud-accruals',
    'asc-606-revenue-waterfall', 'deferred-revenue-scheduler', 'subledger-revenue-reconciler',
  ]);

  for (const [sectionId, config] of Object.entries(sectionConfigs) as [NonNullable<ActiveSection>, SectionConfig][]) {
    if (MONTHLY_CLOSE_SECTION_IDS.has(sectionId)) continue;
    for (const item of config.items) {
      if ('isGroup' in item) continue;
      if (!item.path || item.path.startsWith('#')) continue;
      const matches = item.exact
        ? pathMatchesExactItem(item.path, path)
        : pathMatchesItem(item.path, path);
      if (!matches) continue;
      if (!isItemPathEnabled(item)) return null;
      return sectionId;
    }
  }

  // Fallback: section base path (only if section has at least one enabled item).
  for (const [sectionId, config] of Object.entries(sectionConfigs) as [NonNullable<ActiveSection>, SectionConfig][]) {
    if (MONTHLY_CLOSE_SECTION_IDS.has(sectionId)) continue;
    if (!pathMatchesBase(config.basePath, path)) continue;
    const hasEnabledItems = config.items.some(item => 'isGroup' in item ? false : isItemPathEnabled(item));
    if (hasEnabledItems) return sectionId;
    return null;
  }

  return null;
}

/**
 * Check if a path belongs to a contextual section
 */
export function isContextualPath(path: string): boolean {
  return getSectionFromPath(path) !== null;
}

/**
 * Get all section IDs that have contextual navigation
 */
export function getContextualSectionIds(): NonNullable<ActiveSection>[] {
  return Object.keys(sectionConfigs) as NonNullable<ActiveSection>[];
}
